/**
 * ChapterTrialCountdown — soft banner at the top of a Section when the user
 * accessed this chapter via a $5 book purchase (10-day trial). Renders null
 * for privileged tiers or when the trial doesn't apply.
 */
import React from "react";
import { Link } from "react-router-dom";
import { Clock, ArrowRight } from "lucide-react";

function daysRemaining(iso) {
  if (!iso) return null;
  const diff = new Date(iso).getTime() - Date.now();
  if (diff <= 0) return 0;
  return Math.ceil(diff / (24 * 60 * 60 * 1000));
}

export default function ChapterTrialCountdown({ sectionId, entitlements }) {
  const trialMap = entitlements?.chapter_trial_access || {};
  const expiresAt = trialMap[sectionId];
  if (!expiresAt) return null;
  // Privileged tiers see the workbook regardless — don't show the trial nudge
  if (entitlements?.subscription_active || entitlements?.listener_lifetime) return null;

  const days = daysRemaining(expiresAt);
  const label = days === 0 ? "expires today" : days === 1 ? "1 day left" : `${days} days left`;

  return (
    <div
      data-testid={`chapter-trial-countdown-${sectionId}`}
      className="mb-6 border border-terracotta/60 bg-terracotta/10 p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4"
    >
      <div className="h-9 w-9 border border-terracotta text-terracotta flex items-center justify-center flex-shrink-0">
        <Clock className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-sans uppercase tracking-[0.22em] text-[0.65rem] text-terracotta mb-1">
          Chapter trial · {label}
        </p>
        <p className="font-serif text-base text-bone-100 leading-snug">
          Your $5 book unlocked this chapter for 10 days. The ebook is yours forever — chapter access ends soon.
        </p>
      </div>
      <Link
        to="/pricing"
        data-testid={`chapter-trial-upgrade-${sectionId}`}
        className="inline-flex items-center gap-2 min-h-[44px] px-4 font-sans text-xs uppercase tracking-[0.2em] text-obsidian-900 bg-terracotta hover:bg-terracotta-deep hover:text-bone-100 transition-colors whitespace-nowrap"
      >
        Keep it forever <ArrowRight className="h-3 w-3" />
      </Link>
    </div>
  );
}
